import { useContext, useEffect, useState } from "react";
import { Empty, Input, Modal as AntModal, Tooltip, message } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  StarFilled,
} from "@ant-design/icons";

import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import AppContext from "../../../../context/AppContext";
import { SIDER_TABS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useCurationDisplayName } from "../../../../hooks/useCurationDisplayName";
import CurationEntityName from "../CurationEntityName";
import {
  useAdminPages,
  useCreateAdminPage,
  useDeleteAdminPage,
  useUpdateAdminPage,
  type AdminPage,
} from "../../../../queries/casino-configuration/views/useAdminViews";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";

import "./stylesPagesConfiguration.css";

const EMPTY_ADD_FORM = { code: "", name: "", description: "" };

function errorMessage(err: unknown, fallback: string): string {
  return (err as Error)?.message || fallback;
}

function PagesConfigurationPage() {
  usePageTitle(AppStrings.CASINO_CONFIG_PAGES_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  const { data: pagesData, isLoading, isError } = useAdminPages();
  const pages: AdminPage[] = pagesData ?? [];

  const { mutateAsync: createPage, isPending: creating } = useCreateAdminPage();
  const { mutateAsync: updatePage, isPending: updating } = useUpdateAdminPage();
  const { mutateAsync: deletePage, isPending: deleting } = useDeleteAdminPage();

  const { getName, getDescription } = useCurationDisplayName();
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [deleteTarget, setDeleteTarget] = useState<AdminPage | null>(null);

  const handleAdd = async () => {
    const code = addForm.code.trim();
    const name = addForm.name.trim();
    if (!code || !name) {
      message.error(AppStrings.CASINO_CONFIG_PAGES_VALIDATION_REQUIRED);
      return;
    }
    try {
      await createPage({
        code,
        name,
        description: addForm.description.trim() || undefined,
      });
      message.success(AppStrings.CASINO_CONFIG_PAGES_CREATE_SUCCESS);
      setAddForm(EMPTY_ADD_FORM);
    } catch (err: unknown) {
      message.error(
        errorMessage(err, AppStrings.CASINO_CONFIG_PAGES_CREATE_FALLBACK),
      );
    }
  };

  const startEdit = (page: AdminPage) => {
    setEditingCode(page.code);
    setEditForm({ name: page.name, description: page.description ?? "" });
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditForm({ name: "", description: "" });
  };

  const handleSaveEdit = async (page: AdminPage) => {
    const name = editForm.name.trim();
    if (!name) {
      message.error(AppStrings.CASINO_CONFIG_PAGES_VALIDATION_REQUIRED);
      return;
    }
    try {
      await updatePage({
        code: page.code,
        name,
        description: editForm.description.trim(),
      });
      message.success(AppStrings.CASINO_CONFIG_PAGES_UPDATE_SUCCESS);
      cancelEdit();
    } catch (err: unknown) {
      message.error(
        errorMessage(err, AppStrings.CASINO_CONFIG_PAGES_UPDATE_FALLBACK),
      );
    }
  };

  const handleToggle = async (page: AdminPage) => {
    try {
      await updatePage({ code: page.code, is_active: !page.is_active });
      message.success(AppStrings.CASINO_CONFIG_PAGES_UPDATE_SUCCESS);
    } catch (err: unknown) {
      message.error(
        errorMessage(err, AppStrings.CASINO_CONFIG_PAGES_UPDATE_FALLBACK),
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePage(deleteTarget.code);
      message.success(AppStrings.CASINO_CONFIG_PAGES_DELETE_SUCCESS);
      setDeleteTarget(null);
    } catch (err: unknown) {
      message.error(
        errorMessage(err, AppStrings.CASINO_CONFIG_PAGES_DELETE_FALLBACK),
      );
    }
  };

  return (
    <>
      <div className="pages-shell">
        <div className="pages-config">
          <div className="pages-header">
            <Tooltip
              placement="bottomLeft"
              title={AppStrings.CASINO_CONFIG_PAGES_INFO}
            >
              <span
                className="pages-title-info"
                aria-label={AppStrings.CASINO_CONFIG_PAGES_INFO_ARIA}
              >
                <InfoCircleOutlined />
              </span>
            </Tooltip>
            <p className="pages-subtitle">
              {AppStrings.CASINO_CONFIG_PAGES_SUBTITLE}
            </p>
          </div>

          <div className="pages-add">
            <div className="pages-field pages-field--code">
              <label className="pages-field-label" htmlFor="pages-add-code">
                {AppStrings.CASINO_CONFIG_PAGES_CODE_LABEL}
              </label>
              <Input
                id="pages-add-code"
                className="pages-input"
                placeholder={AppStrings.CASINO_CONFIG_PAGES_CODE_PLACEHOLDER}
                value={addForm.code}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, code: e.target.value }))
                }
              />
            </div>
            <div className="pages-field pages-field--name">
              <label className="pages-field-label" htmlFor="pages-add-name">
                {AppStrings.CASINO_CONFIG_PAGES_NAME_LABEL}
              </label>
              <Input
                id="pages-add-name"
                className="pages-input"
                placeholder={AppStrings.CASINO_CONFIG_PAGES_NAME_PLACEHOLDER}
                value={addForm.name}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <div className="pages-field pages-field--desc">
              <label className="pages-field-label" htmlFor="pages-add-desc">
                {AppStrings.CASINO_CONFIG_PAGES_DESCRIPTION_LABEL}
              </label>
              <Input
                id="pages-add-desc"
                className="pages-input"
                placeholder={
                  AppStrings.CASINO_CONFIG_PAGES_DESCRIPTION_PLACEHOLDER
                }
                value={addForm.description}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, description: e.target.value }))
                }
                onPressEnter={handleAdd}
              />
            </div>
            <button
              type="button"
              className="pages-add-btn"
              onClick={handleAdd}
              disabled={creating}
            >
              <PlusOutlined /> {AppStrings.CASINO_CONFIG_PAGES_ADD}
            </button>
          </div>

          {isLoading ? (
            <div className="pages-loader">
              <RebetLoader height="48px" width="48px" />
            </div>
          ) : isError ? (
            <div className="pages-empty">
              <Empty
                description={AppStrings.CASINO_CONFIG_PAGES_LOAD_ERROR}
              />
            </div>
          ) : pages.length === 0 ? (
            <div className="pages-empty">
              <Empty description={AppStrings.CASINO_CONFIG_PAGES_EMPTY} />
            </div>
          ) : (
            <div className="pages-list" data-testid="pages-list">
              {pages.map((page) => {
                const isEditing = editingCode === page.code;
                return (
                  <div
                    key={page.code}
                    className={`pages-row ${
                      page.is_active ? "" : "pages-row--inactive"
                    }`}
                  >
                    {isEditing ? (
                      <div className="pages-edit-form">
                        <Input
                          className="pages-input"
                          aria-label={AppStrings.CASINO_CONFIG_PAGES_NAME_LABEL}
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, name: e.target.value }))
                          }
                        />
                        <Input
                          className="pages-input"
                          aria-label={
                            AppStrings.CASINO_CONFIG_PAGES_DESCRIPTION_LABEL
                          }
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              description: e.target.value,
                            }))
                          }
                          onPressEnter={() => handleSaveEdit(page)}
                        />
                        <button
                          type="button"
                          className="pages-edit-save"
                          onClick={() => handleSaveEdit(page)}
                          disabled={updating}
                        >
                          {AppStrings.CASINO_CONFIG_PAGES_SAVE}
                        </button>
                        <button
                          type="button"
                          className="pages-edit-cancel"
                          onClick={cancelEdit}
                        >
                          {AppStrings.CASINO_CONFIG_PAGES_CANCEL}
                        </button>
                      </div>
                    ) : (
                      <div className="pages-row-main">
                        <div className="pages-row-title">
                          <CurationEntityName
                            className="pages-row-name"
                            entity={page}
                            displayName={getName(page)}
                          />
                          <span className="pages-row-code">{page.code}</span>
                          {page.is_default && (
                            <span className="pages-default-badge">
                              <StarFilled />
                              {AppStrings.CASINO_CONFIG_PAGES_DEFAULT_BADGE}
                            </span>
                          )}
                        </div>
                        {page.description && (
                          <p className="pages-row-desc">
                            {getDescription(page)}
                          </p>
                        )}
                      </div>
                    )}

                    {!isEditing && (
                      <div className="pages-row-actions">
                        <button
                          type="button"
                          className="pages-icon-btn"
                          aria-label={AppStrings.CASINO_CONFIG_PAGES_EDIT_ARIA}
                          onClick={() => startEdit(page)}
                        >
                          <EditOutlined />
                        </button>
                        <label className="pages-toggle-wrap">
                          <span className="pages-toggle">
                            <input
                              type="checkbox"
                              checked={page.is_active}
                              disabled={page.is_default || updating}
                              onChange={() => handleToggle(page)}
                            />
                            <span className="pages-toggle-slider" />
                          </span>
                          <span className="pages-status-label">
                            {page.is_active
                              ? AppStrings.CASINO_CONFIG_PAGES_STATUS_ACTIVE
                              : AppStrings.CASINO_CONFIG_PAGES_STATUS_INACTIVE}
                          </span>
                        </label>
                        <button
                          type="button"
                          className="pages-icon-btn pages-icon-btn--danger"
                          aria-label={
                            AppStrings.CASINO_CONFIG_PAGES_DELETE_ARIA
                          }
                          disabled={page.is_default}
                          onClick={() => setDeleteTarget(page)}
                        >
                          <DeleteOutlined />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AntModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        footer={null}
        centered
        width={420}
        className="pages-delete-modal"
        destroyOnHidden
      >
        <h3 className="pages-delete-title">
          {AppStrings.CASINO_CONFIG_PAGES_DELETE_TITLE}
        </h3>
        {deleteTarget && (
          <p className="pages-delete-target">
            <CurationEntityName
              className="pages-delete-target-name"
              entity={deleteTarget}
              displayName={getName(deleteTarget)}
            />
            <span className="pages-delete-target-code">
              {deleteTarget.code}
            </span>
          </p>
        )}
        <div className="pages-delete-actions">
          <button
            type="button"
            className="pages-delete-cancel"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            {AppStrings.CASINO_CONFIG_PAGES_CANCEL}
          </button>
          <button
            type="button"
            className="pages-delete-confirm"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {AppStrings.CASINO_CONFIG_PAGES_DELETE_CONFIRM}
          </button>
        </div>
      </AntModal>
    </>
  );
}

export default PagesConfigurationPage;
